import type {
  AlertChannelConfig,
  AlertChannelKind,
  AlertRuleConfig,
  AlertSeverity,
  AlertThrottle,
  AlertTrigger,
  AlertsConfig,
  RuleSet,
} from '@kkp-fraud/shared';
import { OFFERED_ALERT_CHANNEL_KINDS, WEBHOOK_ID_PATTERN } from '@kkp-fraud/shared';
import { t } from './i18n';

/**
 * Alert Management as configuration.
 *
 * The console edits alert rules and their channels into the draft as `RuleSet.alerts` and
 * releases them through the same propose/approve path as a rule, exactly like the feeder
 * payload (`feederFields.ts`). `effectiveAlerts` picks the draft's copy when it has one,
 * else the built-in examples below.
 *
 * The defaults are three example alert rules sending to three EMAIL channels, each on an event
 * type the alert dispatcher sends (`OFFERED_TRIGGERS`). The addresses are
 * placeholders on the reserved `example.com` domain (RFC 2606) — an Editor replaces them with
 * real ones. Every rule is Off: the dispatcher reads only a document's OWN `alerts`, so a
 * default switched on here would read "Ready" while nothing is sent. Switching one on writes
 * the configuration into the draft, and publishing makes it real.
 *
 * Email-first: the dispatcher delivers `email` and `webhook` channels only, so those are the
 * kinds the console offers (`OFFERED_CHANNEL_KINDS`). An older document may still name
 * `teams`, `cardholder` or `oncall`: it loads, the channel reads "not supported — switch to
 * email", and the channel editor will not save such a kind again.
 *
 * Delivery: the alert dispatcher (packages/alert-dispatcher) sends the `email` channels of
 * enabled `decision` / `rule` / `ruleset` alerts through Azure Communication Services, and the
 * `webhook` channels of `decision` / `rule` alerts. What it can do right now comes from
 * `GET /api/alerts/status` — see `parseAlertsStatus` and `ruleStatus` below.
 */

export const ALERT_TRIGGERS: readonly AlertTrigger[] = ['decision', 'rule', 'degraded', 'latency', 'ruleset', 'dependency'];
/**
 * The event types the alert editor offers: the ones the alert service sends (the dispatcher's
 * `SUPPORTED_TRIGGERS` in `packages/alert-dispatcher/src/match.ts` — `alerts.test.ts` keeps the
 * two equal). A rule a document already has on another type is still shown and editable, with
 * its "not sent" status; it just cannot be newly chosen (clean console, 30 Sep 2026).
 */
export const OFFERED_TRIGGERS: readonly AlertTrigger[] = ['decision', 'rule', 'ruleset'];
export const ALERT_SEVERITIES: readonly AlertSeverity[] = ['critical', 'high', 'medium', 'low'];
export const ALERT_THROTTLES: readonly AlertThrottle[] = ['immediate', 'immediate_no_batch', 'batch_5m', 'batch_15m', 'batch_1h', 'daily'];
/** Every kind a document may name — including the ones nothing delivers (read, never offered). */
export const CHANNEL_KINDS: readonly AlertChannelKind[] = ['teams', 'email', 'cardholder', 'oncall', 'webhook'];
/** The kinds the channel editor offers: the ones the dispatcher delivers. */
export const OFFERED_CHANNEL_KINDS: readonly AlertChannelKind[] = OFFERED_ALERT_CHANNEL_KINDS;
export const isOfferedKind = (kind: string): boolean => (OFFERED_CHANNEL_KINDS as readonly string[]).includes(kind);
/** The triggers a webhook channel fires on (the dispatcher's `match.ts`): a webhook renders a decision. */
const WEBHOOK_TRIGGERS: readonly AlertTrigger[] = ['decision', 'rule'];

/** Literal keys, so the catalogue's dead-key gate can see every one of them. */
export const TRIGGER_LABEL: Record<AlertTrigger, string> = {
  decision: 'alert.trigger.decision.label',
  rule: 'alert.trigger.rule.label',
  degraded: 'alert.trigger.degraded.label',
  latency: 'alert.trigger.latency.label',
  ruleset: 'alert.trigger.ruleset.label',
  dependency: 'alert.trigger.dependency.label',
};

export const SEVERITY_LABEL: Record<AlertSeverity, string> = {
  critical: 'alert.severity.critical.label',
  high: 'alert.severity.high.label',
  medium: 'alert.severity.medium.label',
  low: 'alert.severity.low.label',
};

export const SEVERITY_CLASS: Record<AlertSeverity, string> = { critical: 'bad', high: 'warn', medium: 'info', low: 'mute' };

export const THROTTLE_LABEL: Record<AlertThrottle, string> = {
  immediate: 'alert.throttle.immediate.label',
  immediate_no_batch: 'alert.throttle.immediate_no_batch.label',
  batch_5m: 'alert.throttle.batch_5m.label',
  batch_15m: 'alert.throttle.batch_15m.label',
  batch_1h: 'alert.throttle.batch_1h.label',
  daily: 'alert.throttle.daily.label',
};

/** Labels for every kind, the unsupported ones too — an older document's channel still reads as what it was. */
export const CHANNEL_KIND_LABEL: Record<AlertChannelKind, string> = {
  teams: 'alert.channel.teams.label',
  email: 'alert.channel.email.label',
  cardholder: 'alert.channel.customer_push.label',
  oncall: 'alert.channel.oncall.label',
  webhook: 'alert.channel.webhook.label',
};

export const DEFAULT_ALERTS: AlertsConfig = {
  // Only triggers the alert service actually sends (decision, named rule, rule set activation).
  rules: [
    {
      id: 'AL-01', name: 'ผล DECLINE จากกฎระดับ P1', enabled: false,
      trigger: 'decision', params: { decisions: ['DECLINE'], minPriority: 'P1' },
      channelIds: ['CH-01'], severity: 'high', throttle: 'immediate',
    },
    {
      id: 'AL-02', name: 'มีกฎ ALERT_ONLY ทำงาน', enabled: false,
      trigger: 'decision', params: { decisions: ['ALERT_ONLY'] },
      channelIds: ['CH-02'], severity: 'medium', throttle: 'batch_15m',
    },
    {
      id: 'AL-03', name: 'มีการเปิดใช้ชุดกฎเวอร์ชันใหม่', enabled: false,
      trigger: 'ruleset', params: {},
      channelIds: ['CH-03'], severity: 'medium', throttle: 'immediate',
    },
  ],
  channels: [
    { id: 'CH-01', kind: 'email', name: 'ทีม Fraud Ops', target: 'fraud-ops@example.com' },
    { id: 'CH-02', kind: 'email', name: 'ทีมวิเคราะห์', target: 'analysts@example.com' },
    { id: 'CH-03', kind: 'email', name: 'เจ้าของกฎและผู้ตรวจสอบ', target: 'rule-owners@example.com' },
  ],
};

/** The alert configuration in force for a document: its own copy when it carries one, else the default. */
export function effectiveAlerts(rs: { alerts?: AlertsConfig } | null | undefined): AlertsConfig {
  return rs?.alerts ?? DEFAULT_ALERTS;
}

export const sameAlerts = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

/** A deep copy — the defaults are shared module state and must never be edited in place. */
export function cloneAlerts(c: AlertsConfig): AlertsConfig {
  return JSON.parse(JSON.stringify(c)) as AlertsConfig;
}

/** `AL-08` after `AL-07` — the next free number for a prefix. */
export function nextAlertId(prefix: 'AL' | 'CH', existing: readonly string[]): string {
  const re = new RegExp(`^${prefix}-(\\d+)$`);
  const max = existing.reduce((m, id) => {
    const hit = id.match(re);
    return hit ? Math.max(m, Number(hit[1])) : m;
  }, 0);
  return `${prefix}-${String(max + 1).padStart(2, '0')}`;
}

/** The alert ids that send to each channel. */
export function channelUsage(config: AlertsConfig): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const r of config.rules) for (const c of r.channelIds) out.set(c, [...(out.get(c) ?? []), r.id]);
  return out;
}

/**
 * R3 webhooks W1: a `webhook` channel names a webhook CONNECTION (kept, with its secrets, by the
 * admin service — never in the rule document): `webhookId`, or a `target` shaped like an id. The
 * same reading as the alert dispatcher's `webhookIdOf`. Null when the channel names none — an
 * older channel that held a URL, say.
 */
export function channelWebhookId(ch: Pick<AlertChannelConfig, 'kind' | 'target' | 'webhookId'>): string | null {
  if (ch.kind !== 'webhook') return null;
  if (typeof ch.webhookId === 'string' && WEBHOOK_ID_PATTERN.test(ch.webhookId)) return ch.webhookId;
  const t = (ch.target ?? '').trim();
  return WEBHOOK_ID_PATTERN.test(t) ? t : null;
}

/** Connection id → the alert rules that send to it through a webhook channel (the admin service's delete guard reads the same). */
export function webhookUsage(config: AlertsConfig): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const ch of config.channels) {
    const id = channelWebhookId(ch);
    if (id === null) continue;
    for (const r of config.rules) {
      if (r.channelIds.includes(ch.id) && !(out.get(id) ?? []).includes(r.id)) out.set(id, [...(out.get(id) ?? []), r.id]);
    }
  }
  return out;
}

/** What a channel row shows as its target: a webhook's connection id (never a URL). */
export function displayTarget(ch: AlertChannelConfig): string {
  if (ch.kind !== 'webhook') return ch.target;
  return channelWebhookId(ch) ?? '—';
}

/** The one-line condition a table row shows, generated from the typed params. */
export function conditionSummary(rule: AlertRuleConfig): string {
  switch (rule.trigger) {
    case 'decision': {
      const base = t('alert.cond.decision', { decisions: rule.params.decisions.join(' | ') || '—' });
      return rule.params.minPriority ? t('alert.cond.decision.priority', { cond: base, priority: rule.params.minPriority }) : base;
    }
    case 'rule':
      return t('alert.cond.rule', { rules: rule.params.ruleIds.join(', ') || '—' });
    case 'degraded':
      return t('alert.cond.degraded', { percent: rule.params.percent, minutes: rule.params.windowMinutes });
    case 'latency':
      return t('alert.cond.latency', { ms: rule.params.p95Ms, minutes: rule.params.minutes });
    case 'ruleset':
      return t('alert.cond.ruleset');
    case 'dependency':
      return t('alert.cond.dependency');
  }
}

/** The params a trigger starts with when the editor switches to it. */
export function defaultParams(trigger: AlertTrigger): AlertRuleConfig['params'] {
  switch (trigger) {
    case 'decision':
      return { decisions: ['DECLINE'] };
    case 'rule':
      return { ruleIds: [] };
    case 'degraded':
      return { percent: 1, windowMinutes: 5 };
    case 'latency':
      return { p95Ms: 100, minutes: 5 };
    default:
      return {};
  }
}

// ---- validation -------------------------------------------------------------

export type AlertProblem = 'id_empty' | 'id_duplicate' | 'name' | 'number' | 'channels' | 'channel_unknown' | 'decisions' | 'rules';
export type ChannelProblem = 'id_empty' | 'id_duplicate' | 'name' | 'kind_unsupported' | 'webhook_missing' | 'webhook_unknown' | 'target_email' | 'target_required';

export const ALERT_PROBLEM_KEY: Record<AlertProblem, string> = {
  id_empty: 'alert.problem.id_empty',
  id_duplicate: 'alert.problem.id_duplicate',
  name: 'alert.problem.name',
  number: 'alert.problem.number',
  channels: 'alert.problem.channels',
  channel_unknown: 'alert.problem.channel_unknown',
  decisions: 'alert.problem.decisions',
  rules: 'alert.problem.rules',
};

export const CHANNEL_PROBLEM_KEY: Record<ChannelProblem, string> = {
  id_empty: 'alert.problem.id_empty',
  id_duplicate: 'alert.problem.id_duplicate',
  name: 'alert.problem.name',
  kind_unsupported: 'alert.problem.kind_unsupported',
  webhook_missing: 'webhooks.channel.problem.missing',
  webhook_unknown: 'webhooks.channel.problem.unknown',
  target_email: 'alert.problem.target_email',
  target_required: 'alert.problem.target_required',
};

const positive = (n: unknown): boolean => typeof n === 'number' && Number.isFinite(n) && n > 0;

/**
 * What stops an alert rule from being applied. `otherIds` are the ids of every OTHER rule
 * in the configuration; `channelIds` every channel that exists.
 */
export function alertProblems(rule: AlertRuleConfig, otherIds: readonly string[], channelIds: readonly string[]): AlertProblem[] {
  const out: AlertProblem[] = [];
  const id = rule.id.trim();
  if (id === '') out.push('id_empty');
  else if (otherIds.includes(id)) out.push('id_duplicate');
  if (rule.name.trim() === '') out.push('name');
  if (rule.trigger === 'degraded' && !(positive(rule.params.percent) && positive(rule.params.windowMinutes))) out.push('number');
  if (rule.trigger === 'latency' && !(positive(rule.params.p95Ms) && positive(rule.params.minutes))) out.push('number');
  if (rule.trigger === 'decision' && rule.params.decisions.length === 0) out.push('decisions');
  if (rule.trigger === 'rule' && rule.params.ruleIds.length === 0) out.push('rules');
  if (rule.channelIds.length === 0) out.push('channels');
  else if (rule.channelIds.some((c) => !channelIds.includes(c))) out.push('channel_unknown');
  return out;
}

const EMAIL = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

/**
 * `connectionIds`: the webhook connections the admin service has — null when unknown (password
 * mode, not loaded yet), in which case only "names no connection" is checked.
 * `requireTarget`: an email channel must name at least one address (switching an unsupported
 * channel to email asks for them); otherwise an empty email list is allowed and reads
 * "No recipient".
 */
export function channelProblems(
  ch: AlertChannelConfig,
  otherIds: readonly string[],
  connectionIds: readonly string[] | null = null,
  requireTarget = false,
): ChannelProblem[] {
  const out: ChannelProblem[] = [];
  const id = ch.id.trim();
  if (id === '') out.push('id_empty');
  else if (otherIds.includes(id)) out.push('id_duplicate');
  if (ch.name.trim() === '') out.push('name');
  const target = ch.target.trim();
  if (!isOfferedKind(ch.kind)) out.push('kind_unsupported');
  if (ch.kind === 'webhook') {
    const webhookId = channelWebhookId(ch);
    if (webhookId === null) out.push('webhook_missing');
    else if (connectionIds !== null && !connectionIds.includes(webhookId)) out.push('webhook_unknown');
  }
  if (ch.kind === 'email' && target !== '' && !target.split(/[,;]/).every((a) => EMAIL.test(a.trim()))) out.push('target_email');
  if (ch.kind === 'email' && requireTarget && target === '') out.push('target_required');
  return out;
}

// ---- delivery: what the alert dispatcher says it can do ----------------------------

/**
 * A channel's delivery state, as the BFF derives it from the alert dispatcher
 * (`GET /api/alerts/status`, dotnet/admin-api `AlertEndpoints`). Never a flag of the
 * console's own: no answer at all is `not_connected`.
 */
export type ChannelDelivery = 'connected' | 'dry_run' | 'disabled' | 'not_connected';

export interface AlertsStatus {
  configured: boolean;
  reachable: boolean;
  /** A test email can be asked for: a dispatcher that answers, and it is not switched off. */
  testEnabled: boolean;
  channels: Record<AlertChannelKind, ChannelDelivery>;
  supportedTriggers: AlertTrigger[];
  senderAddress: string | null;
  testMaxRecipients: number;
}

/** The body of `POST /api/alerts/test-email` on success (200). */
export interface AlertTestResult {
  ok: boolean;
  mode?: 'send' | 'dry_run';
  status?: 'accepted' | 'succeeded' | 'failed' | 'dry_run';
  recipients?: number;
}

const DELIVERIES: readonly ChannelDelivery[] = ['connected', 'dry_run', 'disabled', 'not_connected'];


/**
 * The status body, checked field by field. Anything that is not exactly this shape — an old
 * BFF, a proxy's HTML page, `{}` — is `null`, which the screen shows as not connected.
 */
export function parseAlertsStatus(body: unknown): AlertsStatus | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  const ch = b.channels as Record<string, unknown> | undefined;
  if (!ch || typeof ch !== 'object' || typeof b.testEnabled !== 'boolean') return null;
  const channels = {} as Record<AlertChannelKind, ChannelDelivery>;
  for (const k of CHANNEL_KINDS) {
    const v = ch[k];
    channels[k] = DELIVERIES.includes(v as ChannelDelivery) ? (v as ChannelDelivery) : 'not_connected';
  }
  const triggers = Array.isArray(b.supportedTriggers) ? (b.supportedTriggers.filter((x) => ALERT_TRIGGERS.includes(x as AlertTrigger)) as AlertTrigger[]) : [];
  const max = typeof b.testMaxRecipients === 'number' && b.testMaxRecipients > 0 ? b.testMaxRecipients : 5;
  return {
    configured: b.configured === true,
    reachable: b.reachable === true,
    testEnabled: b.testEnabled,
    channels,
    supportedTriggers: triggers,
    senderAddress: typeof b.senderAddress === 'string' ? b.senderAddress : null,
    testMaxRecipients: max,
  };
}

export function channelDelivery(status: AlertsStatus | null, kind: AlertChannelKind): ChannelDelivery {
  return status?.channels[kind] ?? 'not_connected';
}

/** A channel's target as addresses, with the console's own address rule. Email channels only. */
export function channelAddresses(ch: AlertChannelConfig): string[] {
  if (ch.kind !== 'email') return [];
  return [...new Set(ch.target.split(/[,;]/).map((a) => a.trim()).filter((a) => EMAIL.test(a)))];
}

/** Whether a delivered channel names somebody: an email address, or a webhook connection. */
/** The seeds' placeholder domains (RFC 2606) — an address there is not a recipient yet. */
export const isPlaceholderAddress = (address: string): boolean => /@example\.(com|org|net)$/i.test(address.trim());

/** QA-A-13: the addresses a "Send test" really sends to — a placeholder address is nobody. */
export function testRecipients(ch: AlertChannelConfig): string[] {
  return channelAddresses(ch).filter((a) => !isPlaceholderAddress(a));
}

/**
 * QA-A-09: the webhook connections as the admin service lists them — id → switched on. `null`
 * (or absent) = not known (password mode, still loading): a connection id then counts as a target.
 */
export type ConnectionStates = ReadonlyMap<string, boolean> | null | undefined;

export function channelHasTarget(ch: AlertChannelConfig, connections?: ConnectionStates): boolean {
  if (ch.kind === 'email') return testRecipients(ch).length > 0;
  if (ch.kind === 'webhook') {
    const id = channelWebhookId(ch);
    // A connection that no longer exists is nobody to send to.
    return id !== null && (!connections || connections.has(id));
  }
  return false;
}

/** QA-A-09: a webhook channel whose connection is switched off — the dispatcher skips it. */
function connectionOff(ch: AlertChannelConfig, connections: ConnectionStates): boolean {
  if (ch.kind !== 'webhook' || !connections) return false;
  const id = channelWebhookId(ch);
  return id !== null && connections.get(id) === false;
}

/**
 * What the Status column says — one answer per rule or channel, the first that applies:
 *
 *   unsupported          every channel is a kind nothing delivers (Teams, on-call, cardholder)
 *   no_recipient         no delivered channel names an address / a webhook connection
 *   connection_off       every such channel is a webhook whose connection is switched off (QA-A-09)
 *   off                  the rule is switched off (rules only)
 *   not_connected        the console has no answer from the alert dispatcher
 *   trigger_unsupported  the dispatcher does not evaluate this event type (rules only)
 *   disabled             the dispatcher's kill switch is on
 *   dry_run              the dispatcher logs each message instead of sending it
 *   ready                it would really send
 *
 * Configuration problems come before "off", so an older document's Teams-only rule says what
 * to fix rather than hiding behind Off.
 */
export type AlertStatus = 'unsupported' | 'no_recipient' | 'connection_off' | 'off' | 'not_connected' | 'trigger_unsupported' | 'disabled' | 'dry_run' | 'ready';

export const STATUS_LABEL: Record<AlertStatus, string> = {
  unsupported: 'alert.status.unsupported.chip',
  no_recipient: 'alert.status.no_recipient.chip',
  connection_off: 'alert.status.connection_off.chip',
  off: 'alert.status.off.chip',
  not_connected: 'alert.status.not_connected.chip',
  trigger_unsupported: 'alert.status.trigger_unsupported.chip',
  disabled: 'alert.status.disabled.chip',
  dry_run: 'alert.status.dry_run.chip',
  ready: 'alert.status.ready.chip',
};

export const STATUS_CLASS: Record<AlertStatus, string> = {
  unsupported: 'bad',
  no_recipient: 'warn',
  connection_off: 'warn',
  off: 'mute',
  not_connected: 'mute',
  trigger_unsupported: 'mute',
  disabled: 'bad',
  dry_run: 'warn',
  ready: 'ok',
};

const FROM_DELIVERY: Record<ChannelDelivery, AlertStatus> = { connected: 'ready', dry_run: 'dry_run', disabled: 'disabled', not_connected: 'not_connected' };
/** Best first — a rule with one channel ready and one dry run is ready. */
const DELIVERY_RANK: readonly AlertStatus[] = ['ready', 'dry_run', 'disabled', 'not_connected'];

/** A channel's own status: its kind, its target, then what the dispatcher says about its kind. */
export function channelStatus(ch: AlertChannelConfig, status: AlertsStatus | null, connections?: ConnectionStates): AlertStatus {
  if (!isOfferedKind(ch.kind)) return 'unsupported';
  if (!channelHasTarget(ch, connections)) return 'no_recipient';
  if (connectionOff(ch, connections)) return 'connection_off';
  if (!status?.reachable) return 'not_connected';
  return FROM_DELIVERY[channelDelivery(status, ch.kind)];
}

/**
 * Whether an alert rule actually reaches anybody: a delivered channel with a target, switched
 * on, a dispatcher that answers and evaluates its trigger. Among its channels the best state
 * wins (ready over dry run over switched off).
 */
export function ruleStatus(rule: AlertRuleConfig, config: AlertsConfig, status: AlertsStatus | null, connections?: ConnectionStates): AlertStatus {
  const byId = new Map(config.channels.map((c) => [c.id, c]));
  const channels = rule.channelIds.map((id) => byId.get(id)).filter((c): c is AlertChannelConfig => c !== undefined);
  const offered = channels.filter((c) => isOfferedKind(c.kind));
  if (channels.length > 0 && offered.length === 0) return 'unsupported';
  const reachable = offered.filter((c) => channelHasTarget(c, connections));
  if (reachable.length === 0) return 'no_recipient';
  const targeted = reachable.filter((c) => !connectionOff(c, connections));
  if (targeted.length === 0) return 'connection_off';
  if (!rule.enabled) return 'off';
  if (!status?.reachable) return 'not_connected';
  if (!status.supportedTriggers.includes(rule.trigger)) return 'trigger_unsupported';
  const sending = targeted.filter((c) => c.kind !== 'webhook' || WEBHOOK_TRIGGERS.includes(rule.trigger));
  if (sending.length === 0) return 'trigger_unsupported';
  let best: AlertStatus = 'not_connected';
  for (const c of sending) {
    const s = FROM_DELIVERY[channelDelivery(status, c.kind)];
    if (DELIVERY_RANK.indexOf(s) < DELIVERY_RANK.indexOf(best)) best = s;
  }
  return best;
}

/** The channel an "unsupported" row switches to: same id and name, email, addresses still to give. */
export function asEmailChannel(ch: AlertChannelConfig): AlertChannelConfig {
  return { id: ch.id, kind: 'email', name: ch.name, target: '' };
}

/**
 * The draft after an alerts edit — or `null` when the draft must not be touched at all.
 *
 * Same three cases as the feeder payload's Save: nothing changed → no write (a clean draft
 * stays byte-identical); edited back to exactly what is live while live has no copy of its
 * own → the property is dropped; anything else → the whole configuration as one setting.
 */
export function draftWithAlerts(loaded: RuleSet | null, draft: RuleSet, next: AlertsConfig): RuleSet | null {
  if (sameAlerts(next, effectiveAlerts(draft))) return null;
  if (loaded && loaded.alerts === undefined && sameAlerts(next, DEFAULT_ALERTS)) {
    const { alerts: _gone, ...rest } = draft;
    return rest as RuleSet;
  }
  return { ...draft, alerts: next };
}
